import React, { useState, useEffect } from 'react';
import { careteSubCategory, getAllByCategory } from '../../Constant/Api/Api';
import { useDisclosure } from '@mantine/hooks';
import { Modal, TextInput, Button, Select, TagsInput } from '@mantine/core';
import uploadFile from '../../firebase/firebaseUploadFunction';
import { Helmet } from 'react-helmet';
import { MdDelete } from 'react-icons/md';

const generateSlug = (name) => {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '');
};



function CreateSubCategory({ fetchSubCategoryData }) {
    const [opened, { open, close }] = useDisclosure(false);
    const [subCategoryName, setSubCategoryName] = useState('');
    const [metaSubCategoryName, setMetaSubCategoryName] = useState('');
    const [subCategoryDescription, setSubCategoryDescription] = useState('');
    const [metaSubCategoryDescription, setMetaSubCategoryDescription] = useState('');
    const [subCategoryImage, setSubCategoryImage] = useState(null);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [tags, setTags] = useState([]);
    const [slug, setSlug] = useState('');
    const [imageUploading, setImageUploading] = useState(false);

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        setSlug(generateSlug(subCategoryName));
    }, [subCategoryName]);

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(getAllByCategory, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const data = await response.json();
                setCategories(data);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const handleSubCategoryChange = (event) => {
        setSubCategoryName(event.target.value);
    };

    const handleMetaSubCategoryChange = (event) => {
        setMetaSubCategoryName(event.target.value);
    };

    const handleSubCategoryDescriptionChange = (event) => {
        setSubCategoryDescription(event.target.value);
    };

    const handleMetaSubCategoryDescriptionChange = (event) => {
        setMetaSubCategoryDescription(event.target.value);
    };

    const handleSubCategoryImageChange = (e) => {
        const file = e.target.files[0];

        if (!file) return;

        const maxSize = 100 * 1024;
        if (file.size > maxSize) {
            alert("Image size should not exceed 100KB");
            e.target.value = '';
            return;
        }

        setImageUploading(true);

        uploadFile(e)
            .then((fireBaseUrl) => {
                // Create new image object structure
                const imageObject = {
                    url: fireBaseUrl,
                    metaData: {
                        sizeOfImage: (file.size / 1000).toFixed(2),
                    }
                };
                setSubCategoryImage(imageObject);
                setImageUploading(false);
            })
            .catch((error) => {
                console.error('Error:', error);
                setImageUploading(false);
            });
    };

    const handleDeleteImage = () => {
        setSubCategoryImage(null);
    };

    const handleCategoryChange = (value) => {
        setSelectedCategory(value);
    };

    const clearAllStates = () => {
        setSubCategoryName('');
        setMetaSubCategoryName('');
        setSubCategoryDescription('');
        setMetaSubCategoryDescription('');
        setSubCategoryImage(null);
        setTags([]);
        setSlug('');
        setSelectedCategory('');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        try {
            const token = localStorage.getItem('token');
            console.log("Token being sent:", token); // Debug token

            const formData = {
                name: subCategoryName,
                metaName: metaSubCategoryName || subCategoryName,
                description: subCategoryDescription,
                metaDescription: metaSubCategoryDescription || subCategoryDescription,
                subCategoryImage: subCategoryImage,
                category: selectedCategory,
                tags: tags,
                slug: slug,
            };

            console.log("API endpoint:", careteSubCategory); // Debug endpoint
            console.log("Sending data:", formData);

            const response = await fetch(careteSubCategory, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData),
            });

            console.log("Response status:", response.status);
            const data = await response.json();
            console.log("Response data:", data);

            if (data) {
                clearAllStates();
                close();
                fetchSubCategoryData();
            }
        } catch (error) {
            console.error('Error creating subcategory:', error);
        }
    };

    return (
        <div>
            <Helmet>
                <title>{metaSubCategoryName}</title>
                <meta name="description" content={metaSubCategoryDescription} />
            </Helmet>
            <Button onClick={open}>Create SubCategory</Button>
            <Modal opened={opened} onClose={() => {
                clearAllStates();
                close();
            }} title="Create SubCategory">
                <form onSubmit={handleSubmit}>
                    {/* Image Display Section */}
                    {subCategoryImage && (
                        <div className="mb-4 relative">
                            <img
                                src={subCategoryImage.url}
                                alt="SubCategory"
                                className="w-full h-36 object-cover rounded"
                            />
                            <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs">
                                {subCategoryImage.metaData?.sizeOfImage} KB
                            </div>
                            <button
                                type="button"
                                onClick={handleDeleteImage}
                                className="absolute top-2 left-2 bg-red-500 text-white rounded-full p-1"
                            >
                                <MdDelete size={16} />
                            </button>
                        </div>
                    )}

                    <TextInput
                        label="SubCategory Name"
                        placeholder="Enter subcategory name"
                        value={subCategoryName}
                        onChange={handleSubCategoryChange}
                        required
                    />
                    <TextInput
                        label="Meta SubCategory Name"
                        placeholder="Enter meta subcategory name"
                        value={metaSubCategoryName}
                        onChange={handleMetaSubCategoryChange}
                    />
                    <TextInput
                        label="Description"
                        placeholder="Enter subcategory description"
                        value={subCategoryDescription}
                        onChange={handleSubCategoryDescriptionChange}
                        required
                    />
                    <TextInput
                        label="Meta SubCategory Description"
                        placeholder="Enter meta subcategory description"
                        value={metaSubCategoryDescription}
                        onChange={handleMetaSubCategoryDescriptionChange}
                    />
                    <TextInput
                        label="Slug"
                        placeholder="Slug"
                        value={slug}
                        readOnly
                    />
                    <Select
                        label="Category"
                        data={categories.map((category) => ({
                            label: category.name,
                            value: category._id
                        }))}
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        required
                    />

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            SubCategory Image
                        </label>
                        <input
                            type='file'
                            accept='image/*'
                            placeholder="Select image"
                            onChange={handleSubCategoryImageChange}
                            required={!subCategoryImage}
                            disabled={imageUploading}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        {imageUploading && (
                            <p className="text-sm text-blue-600 mt-2">Uploading image...</p>
                        )}
                    </div>

                    <TagsInput
                        label="Press Enter to submit a tag"
                        placeholder="Enter tag"
                        value={tags}
                        onChange={setTags}
                    />
                    <Button type="submit" disabled={imageUploading}>
                        {imageUploading ? 'Uploading...' : 'Create'}
                    </Button>
                </form>
            </Modal>
        </div>
    );
}

export default CreateSubCategory;